import React, { useEffect, useId, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmSheetProps {
  open: boolean;
  onCancel: () => void;   // safe / dismiss
  onConfirm: () => void;  // destructive
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  tone?: 'danger' | 'default';
  icon?: React.ReactNode;
  children?: React.ReactNode; // identity caption slot
}

// Centered dialog on desktop; slide-up bottom sheet on phones. Reads the viewport
// so the entrance motion matches, guarding matchMedia for jsdom/SSR.
const useIsNarrow = (): boolean => {
  const query = '(max-width: 639px)';
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [narrow, setNarrow] = useState(() => (supported ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!supported) return;
    const mq = window.matchMedia(query);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [supported]);
  return narrow;
};

/**
 * Reusable destructive-confirm surface in the Clinical Editorial system: one calm glass
 * panel, coral warning halo, decisive action. Backdrop click and Escape both dismiss
 * (call onCancel). Heading inherits Playfair via the global h2 rule.
 */
export const ConfirmSheet: React.FC<ConfirmSheetProps> = ({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  icon,
  children,
}) => {
  const reduceMotion = useReducedMotion();
  const isNarrow = useIsNarrow();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const backdropVariants = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
  const panelVariants = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : isNarrow
      ? { initial: { opacity: 0, y: '100%' }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: '100%' } }
      : { initial: { opacity: 0, y: 8, scale: 0.96 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: 8, scale: 0.96 } };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  const isDanger = tone === 'danger';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6"
          variants={backdropVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="relative w-full max-w-none rounded-t-3xl border border-white/60 bg-white/95 px-7 pb-6 pt-5 text-center shadow-clinical-strong outline-none backdrop-blur-md sm:max-w-sm sm:rounded-3xl sm:px-8 sm:pt-7"
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={reduceMotion ? { duration: 0.15 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-clinical-navy/15 sm:hidden" aria-hidden="true" />

            <div className="flex flex-col items-center gap-3">
              <span
                className={`flex h-14 w-14 items-center justify-center rounded-full ${
                  isDanger ? 'bg-[#ff6b6b]/15 text-[#e11d48]' : 'bg-clinical-blue/10 text-clinical-blue'
                }`}
              >
                {icon ?? <ExclamationTriangleIcon className="h-7 w-7" aria-hidden="true" />}
              </span>
              <h2 id={titleId} className="text-xl font-bold text-clinical-navy">
                {title}
              </h2>
              {children && <div className="text-sm">{children}</div>}
              {description && <p className="max-w-[32ch] text-sm text-slate-500">{description}</p>}
            </div>

            <div className="mt-6 flex flex-col gap-2.5 border-t border-clinical-navy/10 pt-5">
              <button
                type="button"
                onClick={onConfirm}
                className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors ${
                  isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-clinical-blue hover:bg-clinical-navy'
                }`}
              >
                {confirmLabel}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="w-full rounded-xl border border-clinical-navy/15 bg-white px-4 py-3 text-sm font-semibold text-clinical-blue transition-colors hover:bg-clinical-blue/5"
              >
                {cancelLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmSheet;
